import React from "react";
import {
  BrowserRouter as Router,
  Routes, Route, Navigate, Outlet
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import LoginScreen from "./screens/LoginScreen";
import PasswordResetForm from "./screens/PasswordResetForm";
import TaskDetailsScreen from "./screens/TaskDetailScreen";
import MainLayout from "./layout/MainLayout";
import TaskPlanning from "./screens/TaskPlanning";
import TaskOverview from "./screens/TaskOverview";
import TaskDetailEditScreen from "./screens/TaskDetailEditScreen";
import ObservationScreen from "./screens/ObservationScreen";
import theme from "./theme";
import LocalStorageUtil from "./utils/localStorageUtil";
import { storageKeys } from "./utils/constants";
import NotFoundScreen from "./screens/NotFoundScreen";
import { AlertProvider } from "./contexts/AlertContext";
import AlertComponent from "./components/AlertComponent";
import { ProjectProvider } from "./contexts/ProjectContext";
import RequestPasswordReset from "./screens/RequestPasswordReset";
import { TitleProvider } from "./contexts/TitleContext";
import { DrawerProvider } from "./contexts/DrawerContext";
import ScrollRestoration from "./components/ScrollRestoration";
import SafetyInstructions from "./screens/SafetyInstructions";
import { TimerProvider } from "./contexts/TimerContext";
import ForegroundPopup from "./components/ForegroundPopup";
import { PopoverProvider } from "./contexts/PopoverContext";
import { AppDataProvider } from "./contexts/AppDataContext";

function App() {

  const ProtectedRoute: React.FC = () => {
    const isAuthenticated = LocalStorageUtil.getItem(storageKeys.isAuthenticated, false);
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    return <Outlet />;
  };

  const PublicRoute: React.FC = () => {
    const isAuthenticated = LocalStorageUtil.getItem(storageKeys.isAuthenticated, false);
    if (isAuthenticated) {
      return <Navigate to="/planning" replace />;
    }
    return <Outlet />;
  };

  const ResetPasswordRoute: React.FC = () => {
    return <PasswordResetForm />;
  };

  return (
    <ProjectProvider>
      <TitleProvider>
        <DrawerProvider>
          <TimerProvider>
            <AppDataProvider>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <CssBaseline />
                  <AlertProvider>
                    <PopoverProvider>
                      <AlertComponent />
                      {/* <InstallPrompt /> */}
                      <Router>
                        <ScrollRestoration />
                        <Routes>
                          <Route element={<PublicRoute />}>
                            <Route path="/login" element={<LoginScreen />} />
                            <Route path="/resetpasswordrequest" element={<RequestPasswordReset />} />
                          </Route>
                          <Route path="/resetpassword/:token" element={<ResetPasswordRoute />} />

                          <Route element={<ProtectedRoute />}>
                            <Route path="/" element={<MainLayout />}>

                              {/*Nested routes for MainLayout */}
                              <Route
                                index
                                element={<Navigate to="/planning" replace />}
                              />
                              <Route path="planning" element={<TaskPlanning />} />
                              <Route path="task" element={<TaskOverview />} />
                              <Route
                                path="taskdetail/:id"
                                element={<TaskDetailsScreen />}
                              />
                              <Route
                                path="updatetaskdetail/:id"
                                element={<TaskDetailEditScreen />}
                              />
                              <Route
                                path="safetyinstructions"
                                element={<SafetyInstructions />}
                              />
                              <Route
                                path="observation"
                                element={<ObservationScreen />}
                              />
                              <Route
                                path="observation/:id"
                                element={<ObservationScreen />}
                              />

                            </Route>
                          </Route>
                          <Route path="*" element={<NotFoundScreen />} />
                        </Routes>
                      </Router>
                      {/* Popup shows when app is in Foreground and Timer is running*/}
                      <ForegroundPopup />
                    </PopoverProvider>
                  </AlertProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </AppDataProvider>
          </TimerProvider>
        </DrawerProvider>
      </TitleProvider>
    </ProjectProvider>
  );
}

export default App;